import React, { useEffect, useRef, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, Upload, ArrowRightLeft, CheckCircle2, AlertTriangle } from "lucide-react";

/**
 * Admin → Migration
 *
 * Two-step flow to copy a full org snapshot from ONE Emergent environment to another
 * (typically Preview → Production).
 *
 * 1. On the source (Preview): click "Download export" → saves a JSON snapshot.
 * 2. On the destination (Production): sign up as admin with the same email, then
 *    upload the JSON here and click "Import". The importer:
 *    - remaps the source admin to YOU (so ownership follows the account)
 *    - reuses existing users by email; inserts new ones with their preview bcrypt hash
 *    - upserts obligations, tasks, timesheets, categories, org settings, etc. by id
 *    - keeps attachment references pointed at Emergent Object Storage (shared blobs)
 */
export default function Migration() {
  const fileRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [includeBlobs, setIncludeBlobs] = useState(false);
  const [result, setResult] = useState(null);
  const [file, setFile] = useState(null);
  const [onlyEmail, setOnlyEmail] = useState("");
  const [orgUsers, setOrgUsers] = useState([]);
  useEffect(() => { api.get("/admin/users").then(({ data }) => setOrgUsers(data)).catch((e) => console.warn(e)); }, []);

  const doExport = async () => {
    setExporting(true);
    try {
      const qs = new URLSearchParams();
      if (includeBlobs) qs.set("include_blobs", "true");
      if (onlyEmail) qs.set("only_obligations_of", onlyEmail);
      const url = `/admin/migration/export${qs.toString() ? `?${qs}` : ""}`;
      const resp = await api.get(url, { responseType: "blob" });
      const objUrl = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = objUrl;
      const who = onlyEmail ? `-obligations-${onlyEmail.split("@")[0]}` : "";
      a.download = `obligo-backup${who}-${new Date().toISOString().slice(0, 10)}${includeBlobs ? "-with-files" : ""}.json`;
      a.click();
      URL.revokeObjectURL(objUrl);
      toast.success(includeBlobs
        ? "Full backup with attachments downloaded."
        : "Backup downloaded — keep it safe.");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setExporting(false); }
  };

  const doImport = async () => {
    if (!file) { toast.error("Pick your backup file first"); return; }
    if (!window.confirm(
      "Restore will merge the backup into THIS organisation.\n\n" +
      "⚠️ Only run this on the URL you want to restore INTO — usually a fresh production URL,\n" +
      "or the same environment if you're rolling back after an accident.\n" +
      "Restoring on top of the exact org the backup came from is refused by the server.\n\n" +
      "Continue?"
    )) return;
    setImporting(true);
    setResult(null);
    try {
      const text = await file.text();
      const snapshot = JSON.parse(text);
      const { data } = await api.post("/admin/migration/import", snapshot);
      setResult(data);
      toast.success("Restore complete!");
    } catch (e) {
      toast.error(formatErr(e));
      setResult({ error: formatErr(e) });
    }
    finally { setImporting(false); }
  };

  return (
    <div>
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-wider text-slate-500">Admin</div>
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Data Backup</h1>
        <p className="text-sm text-slate-500 mt-1 max-w-2xl">
          Copy your entire organisation — users, obligations, tasks, time entries, categories and settings.
          Safe to re-run; existing rows are merged, not duplicated.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
        {/* Export card */}
        <div data-testid="migration-export-card" className="bg-white border border-slate-200 rounded-md p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center justify-center">
              <Download className="w-4 h-4" />
            </div>
            <h2 className="text-lg font-semibold tracking-tight">Step 1 · Export</h2>
          </div>
          <p className="text-sm text-slate-500 mb-3">
            Downloads a JSON backup file of your current org — all users, obligations, tasks, attachments metadata,
            timesheet entries and org settings. Keep this file safe; it's a full snapshot you can restore any time.
          </p>
          <div className="mb-3">
            <div className="text-xs text-slate-500 mb-1">What to export</div>
            <select data-testid="backup-scope" value={onlyEmail} onChange={(e) => setOnlyEmail(e.target.value)}
                    className="w-full max-w-sm border border-slate-200 rounded-md px-3 py-2 text-sm bg-white">
              <option value="">Whole organisation</option>
              {orgUsers.map((u) => (
                <option key={u.id} value={u.email}>Only {u.name || u.email}'s obligations (+ files &amp; payments)</option>
              ))}
            </select>
            {onlyEmail && (
              <p className="text-xs text-slate-500 mt-1" data-testid="backup-scope-note">
                Includes {onlyEmail}'s obligations, their payments and categories. On restore the person is created as an
                Employee with no usable password, so send them a reset link from Admin → Users.
              </p>
            )}
          </div>
          <label className="flex items-center gap-2 mb-4 text-sm text-slate-600 cursor-pointer select-none">
            <input
              type="checkbox"
              data-testid="backup-include-blobs"
              checked={includeBlobs}
              onChange={(e) => setIncludeBlobs(e.target.checked)}
              className="cursor-pointer"
            />
            <span>Include attachment files <span className="text-slate-400">(larger file, self-contained archive)</span></span>
          </label>
          <Button
            data-testid="migration-export-btn"
            onClick={doExport}
            disabled={exporting}
            className="bg-blue-700 hover:bg-blue-800 text-white gap-2"
          >
            <Download className="w-4 h-4" />
            {exporting ? "Preparing…" : (includeBlobs ? "Download backup + files" : "Download backup")}
          </Button>
        </div>

        {/* Import card */}
        <div data-testid="migration-import-card" className="bg-white border border-slate-200 rounded-md p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-md bg-sky-50 text-sky-700 border border-sky-200 flex items-center justify-center">
              <Upload className="w-4 h-4" />
            </div>
            <h2 className="text-lg font-semibold tracking-tight">Step 2 · Restore</h2>
          </div>
          <p className="text-sm text-slate-500 mb-4">
            Upload a previously downloaded backup file to restore your org, or to seed a fresh admin account
            (e.g. on a new URL) with the same data. We'll remap the original admin to you and merge every other row by id.
          </p>
          <input
            ref={fileRef}
            type="file"
            accept="application/json"
            data-testid="migration-file-input"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="block w-full text-sm text-slate-500 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-slate-100 file:text-slate-800 hover:file:bg-slate-200 mb-3"
          />
          <Button
            data-testid="migration-import-btn"
            onClick={doImport}
            disabled={importing || !file}
            className="bg-sky-700 hover:bg-sky-800 text-white gap-2"
          >
            <ArrowRightLeft className="w-4 h-4" /> {importing ? "Restoring…" : "Restore from backup"}
          </Button>
          {file && (
            <div className="text-xs text-slate-500 mt-2 mono truncate" data-testid="migration-file-name">{file.name} · {(file.size/1024).toFixed(0)} KB</div>
          )}
        </div>
      </div>

      {result && (
        <div data-testid="migration-result-card" className="bg-white border border-slate-200 rounded-md p-6">
          <div className="flex items-center gap-2 mb-3">
            {result.error ? (
              <><AlertTriangle className="w-5 h-5 text-rose-600" /><h3 className="text-sm font-semibold text-rose-800">Restore failed</h3></>
            ) : (
              <><CheckCircle2 className="w-5 h-5 text-emerald-600" /><h3 className="text-sm font-semibold">Restore complete</h3></>
            )}
          </div>
          {result.error ? (
            <p className="text-sm text-rose-700">{result.error}</p>
          ) : (
            <ul className="text-sm space-y-1 mono">
              <li>Target org: <b>{result.target_org_id?.slice(0,8)}…</b></li>
              {Object.entries(result.stats || {}).map(([k, v]) => (
                <li key={k}>
                  <span className="text-slate-500">{k}:</span>{" "}
                  {typeof v === "object"
                    ? <span>imported <b>{v.imported}</b>{v.skipped ? <>, skipped <b>{v.skipped}</b></> : null}</span>
                    : <b>{String(v)}</b>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="mt-6 bg-slate-50 border border-slate-200 rounded-md p-5 text-sm">
        <h3 className="font-semibold mb-2">Notes</h3>
        <ul className="space-y-1.5 text-slate-600 list-disc pl-5">
          <li>Restores are <b>idempotent</b> — restoring the same backup twice won't create duplicates. Existing rows are updated in place.</li>
          <li><b>Attachment files</b>: leave the checkbox unchecked for a small metadata-only backup (recommended for regular saves) — files stay in Emergent storage. Tick <b>Include attachment files</b> for a self-contained backup on your laptop (much larger; ideal for cold offline archives).</li>
          <li>Users are matched by <b>email</b>. If a user with the same email already exists, that account is reused and any tasks/obligations get reassigned to it. New emails are created with the exact <b>same passwords</b> they had at the time of backup.</li>
          <li>The <b>original admin</b> in the backup is automatically remapped to <b>you</b> — the account running Step 2.</li>
          <li>Download a fresh backup <b>often</b> — daily or weekly — so you always have a recent restore point.</li>
        </ul>
      </div>
    </div>
  );
}
